import { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { INDEX } from "@/components/landing/FooterSearcher";

// Lupa del header: botón compacto a la izquierda del selector de idioma que
// abre un overlay/modal con buscador instantáneo (JS en vivo) sobre el índice
// curado de páginas, blog, tienda, carriers y legal. Navega al resultado al
// hacer clic (incluye anclas /#seccion con scroll suave).
export default function NavSearchOverlay({ iconClassName }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    setQ("");
  }, [open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    if (open) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return INDEX.filter(it => it.t.toLowerCase().includes(term) || it.g.toLowerCase().includes(term)).slice(0, 10);
  }, [q]);

  const go = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      else navigate(`/#${id}`);
    } else {
      navigate(to);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Buscar en el sitio"
        title="Buscar en el sitio"
        className={`flex items-center justify-center min-h-[40px] min-w-[40px] px-1.5 py-1 ${iconClassName ?? "text-ink-muted"} hover:text-cyan transition-colors shrink-0`}
      >
        <Search className="w-4 h-4" />
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[120] bg-ink/80 backdrop-blur-sm flex items-start justify-center pt-[12vh] px-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-xl surface shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Buscar contenido"
          >
            <div className="flex items-center gap-2 px-3 py-3 border-b border-ink-border focus-within:border-cyan transition-colors">
              <Search className="w-4 h-4 text-cyan shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar páginas, blog, tienda, carriers y legal…"
                className="flex-1 bg-transparent outline-none text-sm text-ink-text placeholder:text-ink-muted/70"
                aria-label="Buscar contenido"
              />
              <button onClick={() => setOpen(false)} aria-label="Cerrar" className="text-ink-muted hover:text-cyan shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto overscroll-contain">
              {q.trim() === "" ? (
                <div className="px-4 py-10 text-center text-xs text-ink-muted">
                  Escribe para buscar en toda la plataforma, blog, tienda y marco legal…
                </div>
              ) : results.length === 0 ? (
                <div className="px-4 py-10 text-center text-xs text-ink-muted">
                  Sin resultados para <span className="text-ink-text font-medium">"{q}"</span>
                </div>
              ) : (
                results.map((r) => (
                  <button
                    key={r.to + r.t}
                    type="button"
                    onClick={() => go(r.to)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-cyan/10 transition-colors border-b border-ink-border/60 last:border-0"
                  >
                    <span className="text-sm text-ink-text truncate">{r.t}</span>
                    <span className="text-[0.6rem] uppercase tracking-wider text-cyan shrink-0">{r.g}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}